import i18n from "../../../i18n";

import DateService from "../../../services/dateService";
import SelectFieldService from "../../../services/selectFieldService";
import WindowService from "../../../services/windowService";

import ModeEditOutlineOutlined from "@mui/icons-material/ModeEditOutlineOutlined";
import FeedOutlinedIcon from "@mui/icons-material/FeedOutlined";
import SyncIcon from "@mui/icons-material/Sync";
import { Tooltip } from "@mui/material";

import {
  AUTOCOMPLETE_FIELD_TYPE,
  TEXT_FIELD_TYPE,
  DATETIME_TO_DATE_RANGE_FIELD_TYPE,
  DATE_RANGE_FIELD_TYPE,
  NUMERIC_RANGE_FIELD_TYPE,
  BOOLEAN_FIELD_TYPE,
} from "../../../helpers/constants";

import { convertFlatListFromBackendToTranslatedOptionObject } from "../../../helpers/methods";
import {
  getStyleForUsageDifferenceCell,
  getStyleForBmsReadingCell,
} from "../../../helpers/styles";

export const EQUIPMENT_USAGE_TABLE_NAME = "equipment_usage";

export const EQUIPMENT_USAGE_TABLE_FIELDS_CONFIG = [
  {
    name: "equipment",
    getValueCallback: (rowData) => rowData.equipment,
    label: i18n.t("table.equipment_usage.equipment"),
    filterType: AUTOCOMPLETE_FIELD_TYPE,
    getFilterFieldConfigCallback: (filteringData) => ({
      options: filteringData?.value?.equipment,
      isObjectOption: true,
      optionLabelKey: "equipment_nr",
      addNewValue: false,
      multiple: true,
    }),
    headerSx: { whiteSpace: "break-spaces" },
    sortable: true,
  },
  {
    name: "is_scheme",
    getValueCallback: (rowData) =>
      rowData.is_scheme ? (
        <Tooltip title={rowData.equipscheme_scripts}>
          {SelectFieldService.getLabelOptionsTrueOrFalse(rowData.is_scheme)}
        </Tooltip>
      ) : (
        SelectFieldService.getLabelOptionsTrueOrFalse(rowData.is_scheme)
      ),
    label: i18n.t("table.equipment_usage.is_scheme"),
    filterType: BOOLEAN_FIELD_TYPE,
    getFilterFieldConfigCallback: (filteringData) => undefined,
  },
  {
    name: "tenant",
    getValueCallback: (rowData) => rowData.tenant,
    label: i18n.t("table.equipment_usage.tenant"),
    filterType: AUTOCOMPLETE_FIELD_TYPE,
    getFilterFieldConfigCallback: (filteringData) => ({
      options: filteringData?.value?.tenant,
      isObjectOption: true,
      optionLabelKey: "tenant_short_name",
      addNewValue: false,
      multiple: true,
    }),
    headerSx: { whiteSpace: "break-spaces" },
  },
  {
    name: "eits_start_settlement",
    getValueCallback: (rowData) =>
      DateService.convertDatetimeFromBackendToFormatYYYYMMDD(
        rowData.eits_start_settlement,
      ),
    filterType: DATE_RANGE_FIELD_TYPE,
    getFilterFieldConfigCallback: (filteringData) => undefined,
    label: i18n.t("table.equipment_usage.eits_start_settlement"),
    headerSx: { whiteSpace: "break-spaces" },
  },
  {
    name: "eits_end_settlement",
    getValueCallback: (rowData) =>
      DateService.convertDatetimeFromBackendToFormatYYYYMMDD(
        rowData.eits_end_settlement,
      ),
    filterType: DATE_RANGE_FIELD_TYPE,
    getFilterFieldConfigCallback: (filteringData) => undefined,
    label: i18n.t("table.equipment_usage.eits_end_settlement"),
    headerSx: { whiteSpace: "break-spaces" },
  },
  {
    name: "objects_level_2_name",
    getValueCallback: (rowData) => rowData.location.level_2?.object_name,
    label: i18n.t("table.equipment_usage.object_level_2"),
    filterType: AUTOCOMPLETE_FIELD_TYPE,
    getFilterFieldConfigCallback: (filteringData) => ({
      options: filteringData.value.objects_level_2_name,
      isObjectOption: false,
      addNewValue: false,
      multiple: true,
    }),
    headerSx: { whiteSpace: "break-spaces" },
  },
  {
    name: "objects_level_3_name",
    getValueCallback: (rowData) => rowData.location.level_3?.object_name,
    label: i18n.t("table.equipment_usage.object_level_3"),
    filterType: AUTOCOMPLETE_FIELD_TYPE,
    getFilterFieldConfigCallback: (filteringData) => ({
      options: filteringData.value.objects_level_3_name,
      isObjectOption: false,
      addNewValue: false,
      multiple: true,
    }),
    headerSx: { whiteSpace: "break-spaces" },
  },
  {
    name: "ppe",
    getValueCallback: (rowData) => rowData.ppe,
    label: i18n.t("table.equipment_usage.ppe"),
    filterType: AUTOCOMPLETE_FIELD_TYPE,
    getFilterFieldConfigCallback: (filteringData) => ({
      options: filteringData?.value?.ppe,
      isObjectOption: true,
      optionLabelKey: "ppe_number",
      addNewValue: false,
      multiple: true,
    }),
    headerSx: { whiteSpace: "break-spaces" },
  },
  {
    name: "is_ppe",
    getValueCallback: (rowData) =>
      SelectFieldService.getLabelOptionsTrueOrFalse(rowData.is_ppe),
    label: i18n.t("table.equipment_usage.is_ppe"),
    filterType: BOOLEAN_FIELD_TYPE,
    headerSx: { whiteSpace: "break-spaces" },
  },
  {
    name: "equipment_media_type",
    getValueCallback: (rowData) => rowData.equipment_media_type,
    label: i18n.t("table.equipment_usage.equipment_media_type"),
    filterType: AUTOCOMPLETE_FIELD_TYPE,
    getFilterFieldConfigCallback: (filteringData) => ({
      options: filteringData?.value?.equipment_media_type,
      isObjectOption: true,
      optionLabelKey: "equmedtyp_name",
      addNewValue: false,
      multiple: true,
    }),
    headerSx: { whiteSpace: "break-spaces" },
  },
  {
    name: "equipment_note_bms",
    getValueCallback: (rowData) => rowData.equipment_note_bms,
    label: i18n.t("table.equipment_usage.equipment_note_bms"),
    filterType: TEXT_FIELD_TYPE,
    getFilterFieldConfigCallback: (filteringData) => undefined,
    headerSx: { whiteSpace: "break-spaces" },
  },
  {
    name: "equipment_status",
    getValueCallback: (rowData) => rowData.equipment_status,
    label: i18n.t("table.equipment_usage.equipment_status"),
    filterType: AUTOCOMPLETE_FIELD_TYPE,
    getFilterFieldConfigCallback: (filteringData) => ({
      options: filteringData?.value?.equipment_status,
      isObjectOption: true,
      optionLabelKey: "equstat_name",
      addNewValue: false,
      multiple: true,
    }),
    headerSx: { whiteSpace: "break-spaces" },
  },
  {
    name: "equipment_bms_start_date",
    getValueCallback: (rowData) =>
      DateService.convertDatetimeFromBackendToFormatYYYYMMDD_hhmmss(
        rowData.equipment_bms_start_date,
      ),
    label: i18n.t("table.equipment_usage.equipment_bms_start_date"),
    filterType: DATETIME_TO_DATE_RANGE_FIELD_TYPE,
    getFilterFieldConfigCallback: (filteringData) => ({
      style: { minWidth: "100px" },
    }),
    headerSx: { whiteSpace: "break-spaces" },
    cellStyle: (rowData) =>
      getStyleForBmsReadingCell(rowData, "equipment_bms_start"),
  },
  {
    name: "equipment_bms_start_value",
    getValueCallback: (rowData) => rowData.equipment_bms_start_value,
    label: i18n.t("table.equipment_usage.equipment_bms_start_value"),
    filterType: NUMERIC_RANGE_FIELD_TYPE,
    getFilterFieldConfigCallback: (filteringData) => undefined,
    headerSx: { whiteSpace: "break-spaces" },
    cellStyle: (rowData) =>
      getStyleForBmsReadingCell(rowData, "equipment_bms_start"),
  },
  {
    name: "equipment_bms_end_date",
    getValueCallback: (rowData) =>
      DateService.convertDatetimeFromBackendToFormatYYYYMMDD_hhmmss(
        rowData.equipment_bms_end_date,
      ),
    label: i18n.t("table.equipment_usage.equipment_bms_end_date"),
    filterType: DATETIME_TO_DATE_RANGE_FIELD_TYPE,
    getFilterFieldConfigCallback: (filteringData) => ({
      style: { minWidth: "100px" },
    }),
    headerSx: { whiteSpace: "break-spaces" },
    cellStyle: (rowData) =>
      getStyleForBmsReadingCell(rowData, "equipment_bms_end"),
  },
  {
    name: "equipment_bms_end_value",
    getValueCallback: (rowData) => rowData.equipment_bms_end_value,
    label: i18n.t("table.equipment_usage.equipment_bms_end_value"),
    filterType: NUMERIC_RANGE_FIELD_TYPE,
    getFilterFieldConfigCallback: (filteringData) => undefined,
    headerSx: { whiteSpace: "break-spaces" },
    cellStyle: (rowData) =>
      getStyleForBmsReadingCell(rowData, "equipment_bms_end"),
  },
  {
    name: "equipment_usage_value",
    getValueCallback: (rowData) => rowData.equipment_usage_value,
    label: i18n.t("table.equipment_usage.equipment_usage_value"),
    filterType: NUMERIC_RANGE_FIELD_TYPE,
    getFilterFieldConfigCallback: (filteringData) => undefined,
    headerSx: { whiteSpace: "break-spaces" },
    cellStyle: (rowData) =>
      getStyleForUsageDifferenceCell(rowData.equipment_usage_value__difference),
  },
  {
    name: "equipment_usage_method",
    getValueCallback: (rowData) =>
      rowData.equipment_usage_method
        ? i18n.t(`backend_choices_list.${rowData.equipment_usage_method}`)
        : null,
    label: i18n.t("table.equipment_usage.equipment_usage_method"),
    filterType: AUTOCOMPLETE_FIELD_TYPE,
    getFilterFieldConfigCallback: (filteringData) => ({
      options: convertFlatListFromBackendToTranslatedOptionObject(
        filteringData.value?.equipment_usage_method,
      ),
      isObjectOption: true,
      multiple: true,
    }),
    headerSx: { whiteSpace: "break-spaces" },
  },
  {
    name: "equipment_usage_unit",
    getValueCallback: (rowData) => rowData.equipment_usage_unit,
    label: i18n.t("table.equipment_usage.equipment_usage_unit"),
    filterType: TEXT_FIELD_TYPE,
    getFilterFieldConfigCallback: (filteringData) => undefined,
    headerSx: { whiteSpace: "break-spaces" },
  },
  {
    name: "usage_value_1_month_behind",
    getValueCallback: (rowData) => rowData.usage_value_1_month_behind,
    label: i18n.t("table.equipment_usage.usage_value_1_month_behind"),
    filterType: null,
    getFilterFieldConfigCallback: (filteringData) => undefined,
    headerSx: { whiteSpace: "break-spaces" },
    cellStyle: (rowData) =>
      getStyleForUsageDifferenceCell(
        rowData.usage_value_1_month_behind__difference,
      ),
  },
  {
    name: "usage_value_2_month_behind",
    getValueCallback: (rowData) => rowData.usage_value_2_month_behind,
    label: i18n.t("table.equipment_usage.usage_value_2_month_behind"),
    filterType: null,
    getFilterFieldConfigCallback: (filteringData) => undefined,
    headerSx: { whiteSpace: "break-spaces" },
    cellStyle: (rowData) =>
      getStyleForUsageDifferenceCell(
        rowData.usage_value_2_month_behind__difference,
      ),
  },
  {
    name: "usage_value_3_month_behind",
    getValueCallback: (rowData) => rowData.usage_value_3_month_behind,
    label: i18n.t("table.equipment_usage.usage_value_3_month_behind"),
    filterType: null,
    getFilterFieldConfigCallback: (filteringData) => undefined,
    headerSx: { whiteSpace: "break-spaces" },
    cellStyle: (rowData) =>
      getStyleForUsageDifferenceCell(
        rowData.usage_value_3_month_behind__difference,
      ),
  },
  {
    name: "usage_value_4_month_behind",
    getValueCallback: (rowData) => rowData.usage_value_4_month_behind,
    label: i18n.t("table.equipment_usage.usage_value_4_month_behind"),
    filterType: null,
    getFilterFieldConfigCallback: (filteringData) => undefined,
    headerSx: { whiteSpace: "break-spaces" },
    cellStyle: (rowData) =>
      getStyleForUsageDifferenceCell(
        rowData.usage_value_4_month_behind__difference,
      ),
  },
  {
    name: "usage_value_5_month_behind",
    getValueCallback: (rowData) => rowData.usage_value_5_month_behind,
    label: i18n.t("table.equipment_usage.usage_value_5_month_behind"),
    filterType: null,
    getFilterFieldConfigCallback: (filteringData) => undefined,
    headerSx: { whiteSpace: "break-spaces" },
    cellStyle: (rowData) =>
      getStyleForUsageDifferenceCell(
        rowData.usage_value_5_month_behind__difference,
      ),
  },
  {
    name: "average_usage_from_6_month",
    getValueCallback: (rowData) => rowData.average_usage_from_6_month,
    label: i18n.t("table.equipment_usage.average_usage_from_6_month"),
    filterType: null,
    getFilterFieldConfigCallback: (filteringData) => undefined,
    headerSx: { whiteSpace: "break-spaces" },
  },
  {
    name: "equipment_usage_verified",
    getValueCallback: (rowData) =>
      SelectFieldService.getLabelOptionsTrueOrFalse(
        rowData.equipment_usage_verified,
      ),
    label: i18n.t("table.equipment_usage.equipment_usage_verified"),
    filterType: BOOLEAN_FIELD_TYPE,
    getFilterFieldConfigCallback: (filteringData) => undefined,
    headerSx: { whiteSpace: "break-spaces" },
  },
  {
    name: "updated",
    getValueCallback: (rowData) =>
      DateService.convertDatetimeFromBackendToFormatYYYYMMDD_hhmmss(
        rowData.updated,
      ),
    label: i18n.t("table.equipment_usage.updated"),
    filterType: DATETIME_TO_DATE_RANGE_FIELD_TYPE,
    getFilterFieldConfigCallback: (filteringData) => ({
      style: { minWidth: "100px" },
    }),
    sortable: true,
    headerSx: { whiteSpace: "break-spaces" },
  },
  {
    name: "equipment_usage_user",
    getValueCallback: (rowData) => rowData.equipment_usage_user,
    label: i18n.t("table.equipment_usage.user"),
    filterType: AUTOCOMPLETE_FIELD_TYPE,
    getFilterFieldConfigCallback: (filteringData) => ({
      options: filteringData.value.equipment_usage_user,
      isObjectOption: true,
      optionLabelKey: "full_name",
      addNewValue: false,
      multiple: true,
    }),
    headerSx: { whiteSpace: "break-spaces" },
  },
  {
    name: "passport",
    getValueCallback: (rowData) => (
      <FeedOutlinedIcon
        onClick={(e) =>
          WindowService.openInNewTab(
            `/counter_passport/${rowData.equipment_id}`,
          )
        }
      />
    ),
    label: i18n.t("passport"),
    filterType: null,
    getFilterFieldConfigCallback: (filteringData) => undefined,
    allowChangeVisibility: false,
    excludedFromExport: true,
  },
  {
    name: "recalculate",
    getValueCallback: (rowData, onClickRecalculate) =>
      rowData.ed_is_accepted || rowData.equipment_usage_verified ? null : (
        <SyncIcon
          onClick={(e) =>
            onClickRecalculate(
              rowData.equipment_id,
              rowData.equipment_usage_year,
              rowData.equipment_usage_month,
              rowData.equipment_scheme,
            )
          }
        />
      ),
    label: i18n.t("recalculate"),
    filterType: null,
    getFilterFieldConfigCallback: (filteringData) => undefined,
    allowChangeVisibility: false,
    excludedFromExport: true,
  },
];

export const EITS_USAGE_TABLE_NAME = "eits_usage";

export const EITS_TABLE_FIELDS_CONFIG = [
  {
    name: "equipment",
    getValueCallback: (rowData) => rowData.equipment,
    label: i18n.t("table.equipment_usage.equipment"),
    filterType: AUTOCOMPLETE_FIELD_TYPE,
    getFilterFieldConfigCallback: (filteringData) => ({
      options: filteringData?.value?.equipment,
      isObjectOption: true,
      optionLabelKey: "equipment_nr",
      addNewValue: false,
      multiple: true,
    }),
    headerSx: { whiteSpace: "break-spaces" },
  },
  {
    name: "is_scheme",
    getValueCallback: (rowData) =>
      rowData.is_scheme ? (
        <Tooltip title={rowData.equipscheme_scripts}>
          {SelectFieldService.getLabelOptionsTrueOrFalse(rowData.is_scheme)}
        </Tooltip>
      ) : (
        SelectFieldService.getLabelOptionsTrueOrFalse(rowData.is_scheme)
      ),
    label: i18n.t("table.equipment_usage.is_scheme"),
    filterType: BOOLEAN_FIELD_TYPE,
    getFilterFieldConfigCallback: (filteringData) => undefined,
  },
  {
    name: "tenant",
    getValueCallback: (rowData) => rowData.tenant,
    label: i18n.t("table.equipment_usage.tenant"),
    filterType: AUTOCOMPLETE_FIELD_TYPE,
    getFilterFieldConfigCallback: (filteringData) => ({
      options: filteringData?.value?.tenant,
      isObjectOption: true,
      optionLabelKey: "tenant_short_name",
      addNewValue: false,
      multiple: true,
    }),
    headerSx: { whiteSpace: "break-spaces" },
  },
  {
    name: "eits_start_settlement",
    getValueCallback: (rowData) =>
      DateService.convertDatetimeFromBackendToFormatYYYYMMDD(
        rowData.eits_start_settlement,
      ),
    getFilterFieldConfigCallback: (filteringData) => undefined,
    label: i18n.t("table.equipment_usage.eits_start_settlement"),
    filterType: null,
    headerSx: { whiteSpace: "break-spaces" },
  },
  {
    name: "eits_end_settlement",
    getValueCallback: (rowData) =>
      DateService.convertDatetimeFromBackendToFormatYYYYMMDD(
        rowData.eits_end_settlement,
      ),
    getFilterFieldConfigCallback: (filteringData) => undefined,
    label: i18n.t("table.equipment_usage.eits_end_settlement"),
    filterType: null,
    headerSx: { whiteSpace: "break-spaces" },
  },
  {
    name: "objects_level_2_name",
    getValueCallback: (rowData) => rowData.location.level_2?.object_name,
    label: i18n.t("table.equipment_usage.object_level_2"),
    filterType: AUTOCOMPLETE_FIELD_TYPE,
    getFilterFieldConfigCallback: (filteringData) => ({
      options: filteringData.value.objects_level_2_name,
      isObjectOption: false,
      addNewValue: false,
      multiple: true,
    }),
    headerSx: { whiteSpace: "break-spaces" },
  },
  {
    name: "objects_level_3_name",
    getValueCallback: (rowData) => rowData.location.level_3?.object_name,
    label: i18n.t("table.equipment_usage.object_level_3"),
    filterType: AUTOCOMPLETE_FIELD_TYPE,
    getFilterFieldConfigCallback: (filteringData) => ({
      options: filteringData.value.objects_level_3_name,
      isObjectOption: false,
      addNewValue: false,
      multiple: true,
    }),
    headerSx: { whiteSpace: "break-spaces" },
  },
  {
    name: "ppe",
    getValueCallback: (rowData) => rowData.ppe,
    label: i18n.t("table.equipment_usage.ppe"),
    filterType: AUTOCOMPLETE_FIELD_TYPE,
    getFilterFieldConfigCallback: (filteringData) => ({
      options: filteringData?.value?.ppe,
      isObjectOption: true,
      optionLabelKey: "ppe_number",
      addNewValue: false,
      multiple: true,
    }),
    headerSx: { whiteSpace: "break-spaces" },
  },
  {
    name: "equipment_media_type",
    getValueCallback: (rowData) => rowData.equipment_media_type,
    label: i18n.t("table.equipment_usage.equipment_media_type"),
    filterType: AUTOCOMPLETE_FIELD_TYPE,
    getFilterFieldConfigCallback: (filteringData) => ({
      options: filteringData?.value?.equipment_media_type,
      isObjectOption: true,
      optionLabelKey: "equmedtyp_name",
      addNewValue: false,
      multiple: true,
    }),
    headerSx: { whiteSpace: "break-spaces" },
  },
  {
    name: "equipment_note_bms",
    getValueCallback: (rowData) => rowData.equipment_note_bms,
    label: i18n.t("table.equipment_usage.equipment_note_bms"),
    filterType: TEXT_FIELD_TYPE,
    getFilterFieldConfigCallback: (filteringData) => undefined,
    headerSx: { whiteSpace: "break-spaces" },
  },
  {
    name: "equipment_status",
    getValueCallback: (rowData) => rowData.equipment_status,
    label: i18n.t("table.equipment_usage.equipment_status"),
    filterType: AUTOCOMPLETE_FIELD_TYPE,
    getFilterFieldConfigCallback: (filteringData) => ({
      options: filteringData?.value?.equipment_status,
      isObjectOption: true,
      optionLabelKey: "equstat_name",
      addNewValue: false,
      multiple: true,
    }),
    headerSx: { whiteSpace: "break-spaces" },
  },
  {
    name: "equipment_bms_start_date",
    getValueCallback: (rowData) =>
      DateService.convertDatetimeFromBackendToFormatYYYYMMDD_hhmmss(
        rowData.equipment_bms_start_date,
      ),
    label: i18n.t("table.equipment_usage.equipment_bms_start_date"),
    filterType: DATETIME_TO_DATE_RANGE_FIELD_TYPE,
    getFilterFieldConfigCallback: (filteringData) => ({
      style: { minWidth: "100px" },
    }),
    headerSx: { whiteSpace: "break-spaces" },
    cellStyle: (rowData) =>
      getStyleForBmsReadingCell(rowData, "equipment_bms_start"),
  },
  {
    name: "equipment_bms_start_value",
    getValueCallback: (rowData) => rowData.equipment_bms_start_value,
    label: i18n.t("table.equipment_usage.equipment_bms_start_value"),
    filterType: NUMERIC_RANGE_FIELD_TYPE,
    getFilterFieldConfigCallback: (filteringData) => undefined,
    headerSx: { whiteSpace: "break-spaces" },
    cellStyle: (rowData) =>
      getStyleForBmsReadingCell(rowData, "equipment_bms_start"),
  },
  {
    name: "equipment_bms_end_date",
    getValueCallback: (rowData) =>
      DateService.convertDatetimeFromBackendToFormatYYYYMMDD_hhmmss(
        rowData.equipment_bms_end_date,
      ),
    label: i18n.t("table.equipment_usage.equipment_bms_end_date"),
    filterType: DATETIME_TO_DATE_RANGE_FIELD_TYPE,
    getFilterFieldConfigCallback: (filteringData) => ({
      style: { minWidth: "100px" },
    }),
    headerSx: { whiteSpace: "break-spaces" },
    cellStyle: (rowData) =>
      getStyleForBmsReadingCell(rowData, "equipment_bms_end"),
  },
  {
    name: "equipment_bms_end_value",
    getValueCallback: (rowData) => rowData.equipment_bms_end_value,
    label: i18n.t("table.equipment_usage.equipment_bms_end_value"),
    filterType: NUMERIC_RANGE_FIELD_TYPE,
    getFilterFieldConfigCallback: (filteringData) => undefined,
    headerSx: { whiteSpace: "break-spaces" },
    cellStyle: (rowData) =>
      getStyleForBmsReadingCell(rowData, "equipment_bms_end"),
  },
  {
    name: "eits_usage_method",
    getValueCallback: (rowData) =>
      rowData.eits_usage_method
        ? i18n.t(`backend_choices_list.${rowData.eits_usage_method}`)
        : null,
    label: i18n.t("table.equipment_usage.equipment_usage_method"),
    filterType: AUTOCOMPLETE_FIELD_TYPE,
    getFilterFieldConfigCallback: (filteringData) => ({
      options: convertFlatListFromBackendToTranslatedOptionObject(
        filteringData.value?.equipment_usage_method,
      ),
      isObjectOption: true,
      multiple: true,
    }),
    headerSx: { whiteSpace: "break-spaces" },
  },
  {
    name: "eits_usage_value",
    getValueCallback: (rowData) => rowData.eits_usage_value,
    label: i18n.t("table.equipment_usage.eits_usage_value"),
    filterType: NUMERIC_RANGE_FIELD_TYPE,
    getFilterFieldConfigCallback: (filteringData) => undefined,
    headerSx: { whiteSpace: "break-spaces" },
    cellStyle: (rowData) =>
      getStyleForUsageDifferenceCell(rowData.eits_usage_value__difference),
  },
  {
    name: "eits_usage_unit",
    getValueCallback: (rowData) => rowData.eits_usage_unit,
    label: i18n.t("table.equipment_usage.equipment_usage_unit"),
    filterType: TEXT_FIELD_TYPE,
    getFilterFieldConfigCallback: (filteringData) => undefined,
    headerSx: { whiteSpace: "break-spaces" },
  },
  {
    name: "usage_value_1_month_behind",
    getValueCallback: (rowData) => rowData.usage_value_1_month_behind,
    label: i18n.t("table.equipment_usage.usage_value_1_month_behind"),
    filterType: null,
    getFilterFieldConfigCallback: (filteringData) => undefined,
    headerSx: { whiteSpace: "break-spaces" },
    cellStyle: (rowData) =>
      getStyleForUsageDifferenceCell(
        rowData.usage_value_1_month_behind__difference,
      ),
  },
  {
    name: "usage_value_2_month_behind",
    getValueCallback: (rowData) => rowData.usage_value_2_month_behind,
    label: i18n.t("table.equipment_usage.usage_value_2_month_behind"),
    filterType: null,
    getFilterFieldConfigCallback: (filteringData) => undefined,
    headerSx: { whiteSpace: "break-spaces" },
    cellStyle: (rowData) =>
      getStyleForUsageDifferenceCell(
        rowData.usage_value_2_month_behind__difference,
      ),
  },
  {
    name: "usage_value_3_month_behind",
    getValueCallback: (rowData) => rowData.usage_value_3_month_behind,
    label: i18n.t("table.equipment_usage.usage_value_3_month_behind"),
    filterType: null,
    getFilterFieldConfigCallback: (filteringData) => undefined,
    headerSx: { whiteSpace: "break-spaces" },
    cellStyle: (rowData) =>
      getStyleForUsageDifferenceCell(
        rowData.usage_value_3_month_behind__difference,
      ),
  },
  {
    name: "usage_value_4_month_behind",
    getValueCallback: (rowData) => rowData.usage_value_4_month_behind,
    label: i18n.t("table.equipment_usage.usage_value_4_month_behind"),
    filterType: null,
    getFilterFieldConfigCallback: (filteringData) => undefined,
    headerSx: { whiteSpace: "break-spaces" },
    cellStyle: (rowData) =>
      getStyleForUsageDifferenceCell(
        rowData.usage_value_4_month_behind__difference,
      ),
  },
  {
    name: "usage_value_5_month_behind",
    getValueCallback: (rowData) => rowData.usage_value_5_month_behind,
    label: i18n.t("table.equipment_usage.usage_value_5_month_behind"),
    filterType: null,
    getFilterFieldConfigCallback: (filteringData) => undefined,
    headerSx: { whiteSpace: "break-spaces" },
    cellStyle: (rowData) =>
      getStyleForUsageDifferenceCell(
        rowData.usage_value_5_month_behind__difference,
      ),
  },
  {
    name: "average_usage_from_6_month",
    getValueCallback: (rowData) => rowData.average_usage_from_6_month,
    label: i18n.t("table.equipment_usage.average_usage_from_6_month"),
    filterType: null,
    getFilterFieldConfigCallback: (filteringData) => undefined,
    headerSx: { whiteSpace: "break-spaces" },
  },
  {
    name: "updated",
    getValueCallback: (rowData) =>
      DateService.convertDatetimeFromBackendToFormatYYYYMMDD_hhmmss(
        rowData.updated,
      ),
    label: i18n.t("table.equipment_usage.updated"),
    filterType: null,
    headerSx: { whiteSpace: "break-spaces" },
  },
  {
    name: "eits_usage_user",
    getValueCallback: (rowData) => rowData.eits_usage_user,
    label: i18n.t("table.equipment_usage.user"),
    filterType: null,
    headerSx: { whiteSpace: "break-spaces" },
  },
  {
    name: "passport",
    getValueCallback: (rowData) => (
      <FeedOutlinedIcon
        onClick={(e) => {
          e.stopPropagation();
          WindowService.openInNewTab(
            `/counter_passport/${rowData.equipment_id}`,
          );
        }}
      />
    ),
    label: i18n.t("passport"),
    filterType: null,
    getFilterFieldConfigCallback: (filteringData) => undefined,
    allowChangeVisibility: false,
    excludedFromExport: true,
  },
  {
    name: "edit",
    getValueCallback: (
      rowData,
      onOpenEitsUsageDialog,
      onOpenEitsUsageForSchemeDialog,
    ) =>
      rowData.ed_is_accepted || rowData.equipment_usage_verified ? null : (
        <ModeEditOutlineOutlined
          onClick={(e) => {
            e.stopPropagation();
            rowData.is_scheme
              ? onOpenEitsUsageForSchemeDialog(rowData.id)
              : onOpenEitsUsageDialog(rowData.id);
          }}
        />
      ),
    label: i18n.t("edit"),
    filterType: null,
    getFilterFieldConfigCallback: (filteringData) => undefined,
    allowChangeVisibility: false,
    excludedFromExport: true,
  },
];

export const getSixMonthBeforeLabels = (monthStr) => {
  let monthLabels = [];
  for (let i = 0; i < 6; i++) {
    monthLabels.push(
      DateService.convertDateToFormatDDMMYYY(
        DateService.getMonthBehind(i, monthStr),
      ).substring(3),
    );
  }
  return monthLabels;
};

const usageFields = [
  "usage_value_1_month_behind",
  "usage_value_2_month_behind",
  "usage_value_3_month_behind",
  "usage_value_4_month_behind",
  "usage_value_5_month_behind",
];

const getUsageMonthsLabelsMapping = (monthStr, usageFieldLabels) => {
  let monthLabels = getSixMonthBeforeLabels(monthStr);

  let usageFieldToMonthMapping = {};
  for (let i = 0; i < usageFieldLabels.length; i++) {
    usageFieldToMonthMapping[usageFieldLabels[i]] = monthLabels[i];
  }

  return usageFieldToMonthMapping;
};

export const getEquipmentUsageMonthsLabelsMapping = (monthStr) => {
  return getUsageMonthsLabelsMapping(monthStr, [
    "equipment_usage_value",
    ...usageFields,
  ]);
};

export const getEitsUsageMonthsLabelsMapping = (monthStr) => {
  return getUsageMonthsLabelsMapping(monthStr, [
    "eits_usage_value",
    ...usageFields,
  ]);
};
